import { getOutputDomainRecommendations } from '../transforms/son-ownership.transform';
import { withCanonicalSonOwners, projectOwnedStorageClusters } from '../transforms/son-output.transform';
import { VCPM } from '../models/project.model';
import { VcfSizingOutput } from '../models/vcf-sizing-output.model';
import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';
import {
  isStretchedByRecommendation,
  filterPrimarySiteClusters,
  countWitnessClusters,
  getFlatStretchedMultiplier,
  buildClusterSummaries as buildClusterSummariesPure,
  buildWorkloadVmCountLookup,
  aggregateDomainInfra,
} from '../transforms/stretched-cluster.transform';
import { getEffectiveWorkloadRecommendation, resolveWorkloadHardwareConfigModes } from './sizing-recalculation.adapter';

/**
 * The three Angular-only inputs the summary math needs, passed in rather than injected - same
 * shape of dependency inversion buildClusterSummaries() already uses for its arch resolver, so
 * this stays a pure adapter alongside report-request.adapter.ts / sizing-recalculation.adapter.ts.
 */
export interface ProjectSummaryDeps {
  /** PCSSizerSerive.vsanArchType */
  vsanArchType: (architecture: string | null | undefined) => string;
  /** ClusterUsageCalculationService.getStorageUsageBreakdown - narrowed to the fields used here */
  getStorageUsageBreakdown: (
    clusters: PcsSizingEngineResponse.Cluster[],
  ) => { usable: number; usedByWorkloads: number; unit: string } | undefined;
  /** ProjectDetailsState.getProductOffering */
  productOfferings: VCPM.ProductOffering[];
}

/**
 * Computes a project's VcfSizingOutput.Summary from its live recommendation data.
 *
 * Ported verbatim from StdGfRecommendationComponent's former private
 * loadManagementDomainSummary/loadWorkloadDomainSummary/loadInfraSummary. It lives here so the
 * NGXS save actions can call it too: the summary the exported report is built from is now
 * recomputed as part of the save itself, instead of depending on that component being mounted and
 * its ngOnChanges firing before the user hits Export (PCS-2516/PCS-2518).
 *
 * The three apply* steps are order-dependent and mutate the summary passed through them:
 * applyInfraSummary aggregates what the other two wrote.
 */
export function computeSummary(project: VCPM.Project, deps: ProjectSummaryDeps): VcfSizingOutput.Summary {
  let summary: VcfSizingOutput.Summary | undefined = undefined;
  summary = applyManagementDomainSummary(summary, project, deps);
  summary = applyWorkloadDomainSummary(summary, project, deps);
  summary = applyInfraSummary(summary, project, deps);
  return summary;
}

export function applyWorkloadDomainSummary(summary: VcfSizingOutput.Summary | undefined, project: VCPM.Project, deps: ProjectSummaryDeps): VcfSizingOutput.Summary {
  if (summary == undefined) {
    summary = new VcfSizingOutput.Summary();
  }
  summary.workloadDomains = [];
  const wlDomainsRecommendations = getOutputDomainRecommendations(project);
  if (wlDomainsRecommendations && Array.isArray(wlDomainsRecommendations) && wlDomainsRecommendations.length > 0) {
    for (let index = 0; index < wlDomainsRecommendations.length; index++) {
      const wld = new VcfSizingOutput.WorkloadDomain();
      const wldRecommendation = wlDomainsRecommendations[index];
      wld.name = wldRecommendation.requirementsName;
      wld.id = wldRecommendation.requirementsId;
      wld.domainDetails = JSON.parse(JSON.stringify(wldRecommendation.domainSummary));
      if (wldRecommendation.components) {
        wld.domainComponents = wldRecommendation.components.filter(e => e.enabled && e.placementType == "WORKLOAD");
      }

      // Get the workload domain input configuration to access clusterType
      const workloadDomainInput = project.data.workloadDomains?.find(d => String(d.id) === String(wldRecommendation.requirementsId));

      // Each workload's effective (live-tier) raw clusters, for the deduped domain-level totals below.
      const effectiveClustersByWorkload: PcsSizingEngineResponse.Cluster[][] = [];
      wld.workloads = wldRecommendation.workloadRecommendations.map(
        w => {
          // Find the matching workload from the input configuration by workloadId (with type coercion)
          const workloadInput = workloadDomainInput?.workloadList?.find((wl: any) => String(wl.id) === String(w.workloadId));
          const clusterType = workloadInput?.clusterConfiguration?.clusterType || 'singleNode';
          const deploymentType: 'HCI' | 'dHCI' = workloadInput?.vcfDeploymentInputs?.deploymentType ?? 'HCI';

          // Resolve to whichever tier/ReadyNode candidate is currently active for this
          // no-preference workload (selected tier, Recalculate result, or the recommended tier
          // by default) - `w.clusters` raw is empty/stale until this resolution, which is why
          // the Recommendation Summary's cluster cards previously didn't show up for these
          // workloads. No-op for readynode-based/cpu-based workloads (no profileRecommendations).
          // Each side's real input mode is resolved independently (this view has no "focused
          // cluster" concept) so a CON/SON side customized only at the sizing wizard input (never
          // Recalculated at output) still resolves to its real sizing instead of a default estimate
          // - see resolveWorkloadHardwareConfigModes's own doc comment.
          const { conMode, otherMode } = resolveWorkloadHardwareConfigModes(workloadInput, workloadDomainInput, project.data.clusterConfigurations);
          const effectiveW = getEffectiveWorkloadRecommendation(w, conMode, otherMode);
          effectiveClustersByWorkload.push(withCanonicalSonOwners(project, workloadInput, effectiveW.clusters));

          // Check if this workload is stretched
          const isStretched = isStretchedByRecommendation(effectiveW.clusters);

          // For stretched clusters, only count PRIMARY site. For non-stretched, exclude WITNESS.
          const clustersToCount = filterPrimarySiteClusters(effectiveW.clusters, isStretched);
          const storageClusters = clustersToCount.filter(c => c.clusterType !== 'CON');

          // Count witness VMs separately - add 1 VM for each witness cluster
          const witnessVmCount = countWitnessClusters(effectiveW.clusters);

          let nodeCount = 0;
          let coresCount = 0;
          let memory: any = 0;
          let vmsCount = 0;
          let witnessVms = 0; // Track witness VMs separately
          let storage: any = 0;
          let memoryUnit = "GB";
          let storageUnit = "GB";
          clustersToCount.forEach(e => memoryUnit = e.serverConfiguration.memory.units == "TB" ? "TB" : "GB");
          storageClusters.forEach(e => storageUnit = e.clusterConfiguration.rawCapacity?.units == "TB" ? "TB" : "GB");
          clustersToCount.forEach((e: any) => nodeCount += parseInt(e.clusterConfiguration.nodesSize.value));
          clustersToCount.forEach((e: any) => vmsCount += parseInt(e.clusterConfiguration.vmsSize.value));
          witnessVms = witnessVmCount; // Store witness VMs separately
          clustersToCount.forEach((e: any) => coresCount += e.serverConfiguration.cores.value * e.clusterConfiguration.nodesSize.value);
          clustersToCount.forEach((e: any) => {memory += (parseFloat(convertResourceValuePerUnit(e.serverConfiguration.memory, memoryUnit)) * e.clusterConfiguration.nodesSize.value);});
          storageClusters.forEach((e: any) => storage += Number(e.clusterConfiguration.rawCapacity?.value || 0));
          let memoryFormatted: string;
          if(memoryUnit == "GB"){
            memoryFormatted = (memory/1000).toFixed(2);
            memoryUnit = "TB";
          } else {
            memoryFormatted = memory.toFixed ? memory.toFixed(2) : String(memory);
          }
          let storageFormatted: string;
          if(storageUnit == "GB"){
            storageFormatted = (parseFloat(storage)/1000).toFixed(2);
            storageUnit = "TB";
          } else {
            storageFormatted = parseFloat(storage).toFixed(2);
          }

          // Usable (raw - overheads) and used-by-workloads storage, for the "Effective Capacity"
          // metric - same clustersToCount scope as the raw storage sum above, so the two figures
          // stay consistent for this workload.
          const storageBreakdown = storageClusters.length ? deps.getStorageUsageBreakdown(storageClusters) : undefined;
          let usableStorageFormatted = '0';
          let usedByWorkloadsStorageFormatted = '0';
          let usableStorageUnit = storageUnit;
          if (storageBreakdown) {
            usableStorageUnit = storageBreakdown.unit == "GB" ? "TB" : storageBreakdown.unit;
            usableStorageFormatted = (storageBreakdown.unit == "GB" ? storageBreakdown.usable / 1000 : storageBreakdown.usable).toFixed(2);
            usedByWorkloadsStorageFormatted = (storageBreakdown.unit == "GB" ? storageBreakdown.usedByWorkloads / 1000 : storageBreakdown.usedByWorkloads).toFixed(2);
          }

          // Per-cluster breakdown (additive to the aggregate above) for the cluster-card UX
          const clusters: VcfSizingOutput.ClusterSummary[] = buildClusterSummariesPure(effectiveW, clustersToCount, deps.vsanArchType, isStretched, { currentDomainId: wld.id, workloadVmCounts: buildWorkloadVmCountLookup(project.data.workloadDomains) });

          return {
            name: w.name,
            type: w.type,
            subType: w.subtype,
            vSANArchitecture: deps.vsanArchType(effectiveW.clusters[0]?.vsanArchitecture),
            clusterType: clusterType,
            isStretched: isStretched,
            witnessVms: witnessVms,
            deploymentType: deploymentType,
            clusters: clusters,
            workloadInfraSummary: {
              hasStorage: storageClusters.length > 0,
              hosts: { value: nodeCount },
              cores: { value: coresCount },
              memory: { value: parseFloat(memoryFormatted), unit: memoryUnit },
              rawStorage: { value: parseFloat(storageFormatted), unit: storageUnit },
              usableStorage: { value: parseFloat(usableStorageFormatted), unit: usableStorageUnit },
              usedByWorkloadsStorage: { value: parseFloat(usedByWorkloadsStorageFormatted), unit: usableStorageUnit },
              vms: { value: vmsCount }
            }
          };
        }
      );
      wld.domainInfra = aggregateDomainInfra(effectiveClustersByWorkload, wldRecommendation.requirementsId, deps.getStorageUsageBreakdown);
      wld.workloadDomainType = wldRecommendation.workloadDomainType;
      summary.workloadDomains.push(wld);
    }
  }
  projectOwnedStorageClusters(project, summary, deps);
  return summary;
}

export function applyManagementDomainSummary(summary: VcfSizingOutput.Summary | undefined, project: VCPM.Project, deps: ProjectSummaryDeps): VcfSizingOutput.Summary {
  if (summary == undefined) {
    summary = new VcfSizingOutput.Summary();
  }
  const mgmtWorkloadRecomm = project.data.output?.recommendation?.managementDomainRecommendation?.result.recommendations[0].workloadRecommendations[0];
  const effectiveMgmtRecomm = mgmtWorkloadRecomm ? getEffectiveWorkloadRecommendation(mgmtWorkloadRecomm) : undefined;
  summary.managementDomain.architecture = effectiveMgmtRecomm?.clusters[0].vsanArchitecture;

  // Check if management domain is stretched (has PRIMARY, SECONDARY, or WITNESS site types).
  // Resolved through getEffectiveWorkloadRecommendation() (selected tier/ReadyNode, or the
  // recommended tier by default) - the raw recomm.clusters is empty/stale for a no-preference
  // workload until this resolution, same gap that previously left the cluster-card carousel
  // empty on the Recommendation Summary page.
  const hasStretchedClusters = project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.some(
    (ele: PcsSizingEngineResponse.Recommendation) =>
      ele.workloadRecommendations.some(recomm => isStretchedByRecommendation(getEffectiveWorkloadRecommendation(recomm).clusters))
  );
  summary.managementDomain.isStretched = hasStretchedClusters;

  const managementDomainComponents = project.data.output?.recommendation?.managementDomainRecommendation?.result.recommendations[0].components.filter(e => e.enabled && (e.domainType)?.toLowerCase() == ("MANAGEMENT").toLowerCase());
  summary.managementDomain.managementComponents = managementDomainComponents? managementDomainComponents: [];
  summary.managementDomain.workloadDomainComponents = getWorkloadDomainComponents(project);
  let nodeCount = 0;
  let coresCount = 0;
  let memory = 0;
  const vmsCount = project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations[0].domainSummary.numberOfVMs;
  let storage = 0;
  let memoryUnit = "GB";
  let storageUnit = "GB";

  // For stretched clusters, only count PRIMARY site. For non-stretched, count all clusters.
  // identify the hightest unit of memory for RAM
  project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.forEach((ele: PcsSizingEngineResponse.Recommendation) => { ele.workloadRecommendations.forEach(recomm => { filterPrimarySiteClusters(getEffectiveWorkloadRecommendation(recomm).clusters, !!hasStretchedClusters).forEach(e => memoryUnit = e.serverConfiguration.memory.units == "TB" ? "TB" : "GB") }) });

   // identify the hightest unit of raw storage ( GB or TB)
  project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.forEach((ele: PcsSizingEngineResponse.Recommendation) => { ele.workloadRecommendations.forEach(recomm => { filterPrimarySiteClusters(getEffectiveWorkloadRecommendation(recomm).clusters, !!hasStretchedClusters).filter(c => c.clusterType !== 'CON').forEach(e => storageUnit = e.clusterConfiguration.rawCapacity?.units == "TB" ? "TB" : "GB") }) });

  // Calculate node count from clusters
  project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.forEach((ele: PcsSizingEngineResponse.Recommendation) => { ele.workloadRecommendations.forEach(recomm => { filterPrimarySiteClusters(getEffectiveWorkloadRecommendation(recomm).clusters, !!hasStretchedClusters).forEach(e => nodeCount += parseFloat(e.clusterConfiguration.nodesSize.value)) }) })
  // Cores, memory, and storage calculations
  project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.forEach((ele: PcsSizingEngineResponse.Recommendation) => { ele.workloadRecommendations.forEach(recomm => { filterPrimarySiteClusters(getEffectiveWorkloadRecommendation(recomm).clusters, !!hasStretchedClusters).forEach(e => coresCount += e.serverConfiguration.cores.value * parseFloat(e.clusterConfiguration.nodesSize.value)) }) });
  project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.forEach((ele: PcsSizingEngineResponse.Recommendation) => { ele.workloadRecommendations.forEach(recomm => { filterPrimarySiteClusters(getEffectiveWorkloadRecommendation(recomm).clusters, !!hasStretchedClusters).forEach(e => memory += (parseFloat(convertResourceValuePerUnit(e.serverConfiguration.memory, memoryUnit)) * parseFloat(e.clusterConfiguration.nodesSize.value))) }) });
  project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.forEach((ele: PcsSizingEngineResponse.Recommendation) => { ele.workloadRecommendations.forEach(recomm => { filterPrimarySiteClusters(getEffectiveWorkloadRecommendation(recomm).clusters, !!hasStretchedClusters).filter(c => c.clusterType !== 'CON').forEach(e => storage += (parseFloat(e.clusterConfiguration.rawCapacity?.value?.toString() || '0'))) }) });

  // Usable (raw - overheads) and used-by-workloads storage, for the "Effective Capacity" metric -
  // same clustersToCount scope (per recomm) as the raw storage sum above.
  let usableStorage = 0;
  let usedByWorkloadsStorage = 0;
  project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.forEach((ele: PcsSizingEngineResponse.Recommendation) => {
    ele.workloadRecommendations.forEach(recomm => {
      const clustersToCount = filterPrimarySiteClusters(getEffectiveWorkloadRecommendation(recomm).clusters, !!hasStretchedClusters);
      const breakdown = deps.getStorageUsageBreakdown(clustersToCount.filter(c => c.clusterType !== 'CON'));
      if (breakdown) {
        usableStorage += breakdown.usable;
        usedByWorkloadsStorage += breakdown.usedByWorkloads;
      }
    });
  });

  summary.managementDomain.domainInfraSummary = new VcfSizingOutput.InfraSummary();
  summary.managementDomain.domainInfraSummary.hosts.value = nodeCount;
  summary.managementDomain.domainInfraSummary.cores.value = coresCount;
  summary.managementDomain.domainInfraSummary.memory.value = ( memoryUnit == "GB"?(memory / 1000).toFixed(2): memory.toFixed(2));
  summary.managementDomain.domainInfraSummary.memory.unit = "TB";
  summary.managementDomain.domainInfraSummary.vms.value = vmsCount;
  summary.managementDomain.domainInfraSummary.rawStorage.value = (storageUnit == "GB"?(storage / 1000).toFixed(2): storage.toFixed(2));
  summary.managementDomain.domainInfraSummary.rawStorage.unit = "TB";
  summary.managementDomain.domainInfraSummary.hasStorage = project.data.output?.recommendation?.managementDomainRecommendation?.result?.recommendations.some(r =>
    r.workloadRecommendations.some(w => getEffectiveWorkloadRecommendation(w).clusters.some(c => c.siteType !== 'WITNESS' && c.clusterType !== 'CON'))
  ) ?? false;
  summary.managementDomain.domainInfraSummary.usableStorage.value = (storageUnit == "GB"?(usableStorage / 1000).toFixed(2): usableStorage.toFixed(2));
  summary.managementDomain.domainInfraSummary.usableStorage.unit = "TB";
  summary.managementDomain.domainInfraSummary.usedByWorkloadsStorage.value = (storageUnit == "GB"?(usedByWorkloadsStorage / 1000).toFixed(2): usedByWorkloadsStorage.toFixed(2));
  summary.managementDomain.domainInfraSummary.usedByWorkloadsStorage.unit = "TB";

  // Count witness appliances for management domain
  if (hasStretchedClusters) {
    let witnessCount = 0;
    project.data?.output?.recommendation?.managementDomainRecommendation?.result.recommendations.forEach((ele: PcsSizingEngineResponse.Recommendation) => {
      ele.workloadRecommendations.forEach(recomm => {
        const effectiveClusters = getEffectiveWorkloadRecommendation(recomm).clusters;
        witnessCount += countWitnessClusters(effectiveClusters); // Each witness cluster counts as 1 appliance
      });
    });
    summary.managementDomain.witnessVms = witnessCount;
  }

  return summary;
}

export function applyInfraSummary(summary: VcfSizingOutput.Summary | undefined, project: VCPM.Project, deps: ProjectSummaryDeps): VcfSizingOutput.Summary {
  const projectData: VCPM.ProjectData = project?.data;
  if (summary == undefined) {
    summary = new VcfSizingOutput.Summary();
  }

  /* Infra Summary - Simply sum the displayed domain summary values */
  let nodeCount = 0;
  let coresCount = 0;
  let memory = 0;
  let vmsCount = 0;
  let storage = 0;
  let usableStorage = 0;
  let usedByWorkloadsStorage = 0;

  if (projectData.output) {
    // Add management domain values (these are already correctly calculated in applyManagementDomainSummary)
    // For stretched domains, the summary already contains the aggregate with proper multiplication
    if (summary.managementDomain && summary.managementDomain.domainInfraSummary) {
      const mgmtSummary = summary.managementDomain.domainInfraSummary;
      const isMgmtStretched = summary.managementDomain.isStretched;

      // For stretched management: hosts, cores, memory, and storage need to be multiplied by 2 (per-site values)
      // BUT numberOfVMs from backend is already the aggregate (PRIMARY + SECONDARY), so don't multiply
      // For non-stretched: all values are already correct
      if (isMgmtStretched) {
        nodeCount += (parseFloat(String(mgmtSummary.hosts.value)) || 0) * 2;
        coresCount += (parseFloat(String(mgmtSummary.cores.value)) || 0) * 2;

        const mgmtMemory = parseFloat(String(mgmtSummary.memory.value)) || 0;
        memory += (mgmtSummary.memory.unit === 'GB' ? mgmtMemory / 1000 : mgmtMemory) * 2;

        // VMs: numberOfVMs from backend is already aggregate (PRIMARY + SECONDARY), don't multiply
        vmsCount += parseFloat(String(mgmtSummary.vms.value)) || 0;

        const mgmtStorage = parseFloat(String(mgmtSummary.rawStorage.value)) || 0;
        storage += (mgmtSummary.rawStorage.unit === 'GB' ? mgmtStorage / 1000 : mgmtStorage) * 2;

        // usableStorage/usedByWorkloadsStorage may be absent on summaries saved before this
        // feature existed - default to 0 rather than throwing on undefined.
        const mgmtUsableStorage = parseFloat(String(mgmtSummary.usableStorage?.value)) || 0;
        usableStorage += (mgmtSummary.usableStorage?.unit === 'GB' ? mgmtUsableStorage / 1000 : mgmtUsableStorage) * 2;
        const mgmtUsedByWorkloadsStorage = parseFloat(String(mgmtSummary.usedByWorkloadsStorage?.value)) || 0;
        usedByWorkloadsStorage += (mgmtSummary.usedByWorkloadsStorage?.unit === 'GB' ? mgmtUsedByWorkloadsStorage / 1000 : mgmtUsedByWorkloadsStorage) * 2;
      } else {
        nodeCount += parseFloat(String(mgmtSummary.hosts.value)) || 0;
        coresCount += parseFloat(String(mgmtSummary.cores.value)) || 0;

        const mgmtMemory = parseFloat(String(mgmtSummary.memory.value)) || 0;
        memory += mgmtSummary.memory.unit === 'GB' ? mgmtMemory / 1000 : mgmtMemory;

        vmsCount += parseFloat(String(mgmtSummary.vms.value)) || 0;

        const mgmtStorage = parseFloat(String(mgmtSummary.rawStorage.value)) || 0;
        storage += mgmtSummary.rawStorage.unit === 'GB' ? mgmtStorage / 1000 : mgmtStorage;

        const mgmtUsableStorage = parseFloat(String(mgmtSummary.usableStorage?.value)) || 0;
        usableStorage += mgmtSummary.usableStorage?.unit === 'GB' ? mgmtUsableStorage / 1000 : mgmtUsableStorage;
        const mgmtUsedByWorkloadsStorage = parseFloat(String(mgmtSummary.usedByWorkloadsStorage?.value)) || 0;
        usedByWorkloadsStorage += mgmtSummary.usedByWorkloadsStorage?.unit === 'GB' ? mgmtUsedByWorkloadsStorage / 1000 : mgmtUsedByWorkloadsStorage;
      }
    }

    // Add workload domain banner values (these already include stretched multipliers and witness VMs)
    // The domain banner is calculated in std-gf-workload-domain-summary-card.component.ts
    if (summary.workloadDomains && summary.workloadDomains.length > 0) {
      summary.workloadDomains.forEach(domain => {
        // Calculate domain banner aggregate for this domain
        let domainHosts = 0;
        let domainCores = 0;
        let domainMemory = 0;
        let domainVms = 0;
        let domainStorage = 0;
        let domainUsableStorage = 0;
        let domainUsedByWorkloadsStorage = 0;

        domain.workloads.forEach(workload => {
          if (workload && workload.workloadInfraSummary) {
            const wl = workload.workloadInfraSummary;
            const isWlStretched = workload.clusterType === 'stretched';
            const wlMultiplier = getFlatStretchedMultiplier(isWlStretched);

            domainHosts += (wl.hosts.value || 0) * wlMultiplier;
            domainCores += (wl.cores.value || 0) * wlMultiplier;

            const wlMemory = parseFloat(String(wl.memory.value)) || 0;
            const wlMemoryInTB = wl.memory.unit === 'GB' ? wlMemory / 1000 : wlMemory;
            domainMemory += wlMemoryInTB * wlMultiplier;

            // VMs: multiply PRIMARY by multiplier (witness VMs NOT included)
            const wlPrimaryVms = wl.vms.value || 0;
            domainVms += wlPrimaryVms * wlMultiplier;

            const wlStorage = parseFloat(String(wl.rawStorage.value)) || 0;
            const wlStorageInTB = wl.rawStorage.unit === 'GB' ? wlStorage / 1000 : wlStorage;
            domainStorage += wlStorageInTB * wlMultiplier;

            // usableStorage/usedByWorkloadsStorage may be absent on summaries saved before this
            // feature existed - default to 0 rather than throwing on undefined.
            const wlUsableStorage = parseFloat(String(wl.usableStorage?.value)) || 0;
            const wlUsableStorageInTB = wl.usableStorage?.unit === 'GB' ? wlUsableStorage / 1000 : wlUsableStorage;
            domainUsableStorage += wlUsableStorageInTB * wlMultiplier;

            const wlUsedByWorkloadsStorage = parseFloat(String(wl.usedByWorkloadsStorage?.value)) || 0;
            const wlUsedByWorkloadsStorageInTB = wl.usedByWorkloadsStorage?.unit === 'GB' ? wlUsedByWorkloadsStorage / 1000 : wlUsedByWorkloadsStorage;
            domainUsedByWorkloadsStorage += wlUsedByWorkloadsStorageInTB * wlMultiplier;
          }
        });

        // Deduped, ownership-aware totals replace the per-workload sums above (see aggregateDomainInfra).
        if (domain.domainInfra) {
          domainHosts = domain.domainInfra.hosts.value;
          domainCores = domain.domainInfra.cores.value;
          domainMemory = domain.domainInfra.memory.value;
          domainVms = domain.domainInfra.vms.value;
          domainStorage = domain.domainInfra.rawStorage.value;
          if (domain.domainInfra.hasStorage !== undefined || Number(domain.domainInfra.usableStorage?.value) > 0) {
            domainUsableStorage = domain.domainInfra.usableStorage.value;
            domainUsedByWorkloadsStorage = Number(domain.domainInfra.usedByWorkloadsStorage?.value) || 0;
          }
        }

        nodeCount += domainHosts;
        coresCount += domainCores;
        memory += domainMemory;
        vmsCount += domainVms;
        storage += domainStorage;
        usableStorage += domainUsableStorage;
        usedByWorkloadsStorage += domainUsedByWorkloadsStorage;
      });
    }
  }

  summary.vcfDeploymentInfraSummary = new VcfSizingOutput.InfraSummary();
  const storageScopes = [
    summary.managementDomain?.domainInfraSummary,
    ...(summary.workloadDomains ?? []).flatMap(d => d.domainInfra ? [d.domainInfra] : d.workloads.map(w => w.workloadInfraSummary)),
  ].filter(s => !!s);
  summary.vcfDeploymentInfraSummary.hasStorage = storageScopes.some(s => s.hasStorage !== false);
  summary.vcfDeploymentInfraSummary.hosts.value = nodeCount;
  summary.vcfDeploymentInfraSummary.cores.value = coresCount;
  summary.vcfDeploymentInfraSummary.memory.value = memory.toFixed(2);
  summary.vcfDeploymentInfraSummary.memory.unit = "TB";
  summary.vcfDeploymentInfraSummary.vms.value = vmsCount;
  summary.vcfDeploymentInfraSummary.rawStorage.value = storage.toFixed(2);
  summary.vcfDeploymentInfraSummary.rawStorage.unit = "TB";
  summary.vcfDeploymentInfraSummary.usableStorage.value = usableStorage.toFixed(2);
  summary.vcfDeploymentInfraSummary.usableStorage.unit = "TB";
  summary.vcfDeploymentInfraSummary.usedByWorkloadsStorage.value = usedByWorkloadsStorage.toFixed(2);
  summary.vcfDeploymentInfraSummary.usedByWorkloadsStorage.unit = "TB";
  /* Infra Sumary - End */

  /* Product Info - Start */
  summary.productInfo.productName = getFullProductName(project.metadata.product, deps.productOfferings);
  summary.productInfo.version = project.metadata.version;
  summary.productInfo.availableStorageEntitlement = calculateEntitlement(coresCount);
  summary.productInfo.entitlementDescription = getEntitlementDetails()
  /* Product Info - End */

  return summary;
}

// Returns undefined when the matched offering has no fullName - preserved verbatim from the
// component original rather than falling back to the code, since ProductInfo.productName is optional.
export function getFullProductName(productName: string, productOfferings: VCPM.ProductOffering[]): string | undefined {
  const matchedProduct = productOfferings?.find(e => e.name == productName);
  return matchedProduct ? matchedProduct.fullName : productName;
}

export function convertResourceValuePerUnit(resource: PcsSizingEngineResponse.StrValueUnit, unit: string): string {
  if (unit == resource.units) {
    // no conversion needed
    return resource.value;
  }
  else if (unit == "TB" && resource.units == "GB") {
    return ((parseFloat(resource.value) / 1000).toFixed(2)).toString();
  }
  else if (unit == "GB" && resource.units == "TB") {
    return ((parseFloat(resource.value) * 1000).toFixed(2)).toString();
  }
  return resource.value;
}

/**
 * Gets workload domain components (domainType: 'WORKLOAD', placementType: 'MANAGEMENT')
 * from both old and new sources for backward compatibility
 */
export function getWorkloadDomainComponents(project: VCPM.Project): PcsSizingEngineResponse.SoftwareComponent[] {
  // OLD SOURCE (backward compatibility): managementDomainRecommendation components
  const oldSourceComponents = project.data.output?.recommendation?.managementDomainRecommendation?.result.recommendations[0]?.components?.filter(e =>
    e.enabled &&
    (e.domainType)?.toLowerCase() === "workload" &&
    (e.placementType)?.toLowerCase() === "management"
  ) || [];

  // NEW SOURCE (primary): workloadDomainsRecommendation components from ALL workload domains
  const newSourceComponents: PcsSizingEngineResponse.SoftwareComponent[] = [];
  const wldRecommendations = project.data.output?.recommendation?.workloadDomainsRecommendation?.result?.recommendations || [];

  wldRecommendations.forEach((recommendation: PcsSizingEngineResponse.Recommendation) => {
    if (recommendation.components) {
      const wldMgmtComponents = recommendation.components.filter((e: PcsSizingEngineResponse.SoftwareComponent) =>
        e.enabled &&
        (e.domainType)?.toLowerCase() === "workload" &&
        (e.placementType)?.toLowerCase() === "management"
      );

      // Add all workload domain components - groupComponents will handle counting
      newSourceComponents.push(...wldMgmtComponents);
    }
  });

  // Prioritize new source, fallback to old source
  // Don't deduplicate - we want to count all instances across domains
  // The groupComponents method will handle grouping by productName + brickSize
  return newSourceComponents.length > 0 ? newSourceComponents : oldSourceComponents;
}

export function calculateEntitlement(coresCount: number): VcfSizingOutput.ValueUnit {
  return { value: coresCount * 1, unit: "TiB" };
}

export function getEntitlementDetails(): string {
  return "1 TiB / core";
}

